import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import styles from "./PrivateCustomerDetail.module.css";
import AddPrivateCustomerModal from "../../../../../components/Customers/AddPrivateCustomerModal";
import { usePrivateCustomerById, useUpdatePrivateCustomer } from "../../../../../api/people/usePeopleApi";

const mapApiToUi = (api, fromState = {}) => {
  if (!api) return null;
  const addr = {
    street: api.street ?? "",
    houseNumber: api.houseNumber ?? "",
    boxNumber: api.boxNumber ?? "",
    city: api.city ?? "",
    zip: api.postalCode ?? "",
    country: api.country ?? "",
  };
  const financials = {
    totalSpent: parseFloat(api.totalSpent ?? 0),
    openBalance: parseFloat(api.openBalance ?? 0),
    creditNotes: fromState.financials?.creditNotes ?? 0,
    lastPayment: fromState.financials?.lastPayment ?? "",
    lastPaymentAmount: fromState.financials?.lastPaymentAmount ?? 0,
  };
  return {
    id: api.id,
    ref: api.customerCode ?? api.id,
    createdAt: api.createdAt
      ? new Date(api.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "",
    firstName: api.firstName ?? "",
    lastName: api.lastName ?? "",
    email: api.email ?? "",
    phone: api.phone ?? "",
    nationality: api.nationality ?? "",
    dateOfBirth: api.dateOfBirth ?? "",
    gender: api.gender ? api.gender.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : "",
    language: api.preferredLanguage ?? "",
    status: api.status === "ACTIVE" ? "Active" : "Inactive",
    address: addr,
    financials,
    totalBookings: api.totalBookings ?? 0,
    bookings: fromState.bookings ?? [],
    creditNotes: fromState.creditNotes ?? [],
    notes: [],
  };
};

const mockCustomer = {
  id: 1,
  ref: "CUST-PRV-00041",
  createdAt: "03 Jan 2025",
  firstName: "Sarah",
  lastName: "Johnson",
  email: "sarah.j@email.com",
  phone: "+32 470 123 456",
  nationality: "Belgian",
  dateOfBirth: "12 Aug 1985",
  gender: "Female",
  language: "English",
  status: "Active",
  address: {
    street: "Rue de la Loi",
    houseNumber: "42",
    boxNumber: "",
    city: "Brussels",
    zip: "1000",
    country: "Belgium",
  },
  financials: {
    totalSpent: 9840.00,
    openBalance: 1250.00,
    creditNotes: 320.00,
    lastPayment: "14 Mar 2025",
    lastPaymentAmount: 3840.00,
  },
  bookings: [
    {
      ref: "SNK-2025-00124", type: "Package",
      firstService: "14 Mar 2025", lastService: "21 Mar 2025",
      totalPrice: 3840.00, paymentStatus: "Paid", bookingStatus: "Confirmed",
    },
    {
      ref: "SNK-2025-00098", type: "Flight",
      firstService: "10 Nov 2024", lastService: "10 Nov 2024",
      totalPrice: 1250.00, paymentStatus: "Paid", bookingStatus: "Confirmed",
    },
    {
      ref: "SNK-2024-00061", type: "Hotel",
      firstService: "20 Jul 2024", lastService: "27 Jul 2024",
      totalPrice: 2100.00, paymentStatus: "Partial", bookingStatus: "Confirmed",
    },
    {
      ref: "SNK-2024-00033", type: "Package",
      firstService: "05 Apr 2024", lastService: "12 Apr 2024",
      totalPrice: 2650.00, paymentStatus: "Paid", bookingStatus: "Confirmed",
    },
  ],
  creditNotes: [
    { ref: "CN-2025-00012", amount: 320.00, reason: "Hotel room upgrade credit", date: "21 Mar 2025", status: "Available" },
  ],
  notes: [
    { id: 1, author: "Marie L.", date: "15 Mar 2025", text: "Customer requested window seat preferences for all future flights. Noted in profile." },
    { id: 2, author: "Admin", date: "03 Jan 2025", text: "Customer registered via online portal. Verified email confirmed." },
  ],
};

const TYPE_STYLES = {
  Package: { bg: "#eff6ff", color: "#2563eb" },
  Flight:  { bg: "#f0fdf4", color: "#16a34a" },
  Hotel:   { bg: "#fdf4ff", color: "#9333ea" },
};
const TYPE_ICON     = { Package: "📦", Flight: "✈️", Hotel: "🏨" };
const PAYMENT_BADGE = { Paid: styles.payPaid, Partial: styles.payPartial, Unpaid: styles.payUnpaid };
const STATUS_BADGE  = { Confirmed: styles.statusConfirmed, Pending: styles.statusPending, Cancelled: styles.statusCancelled };

const InfoRow = ({ label, value }) => (
  <div className={styles.infoRow}>
    <span className={styles.infoLabel}>{label}</span>
    <span className={styles.infoValue}>{value || "—"}</span>
  </div>
);

const StatCard = ({ label, value, sub, accent }) => (
  <div className={`${styles.statCard} ${accent ? styles.statCardAccent : ""}`}>
    <div className={styles.statValue}>{value}</div>
    <div className={styles.statLabel}>{label}</div>
    {sub && <div className={styles.statSub}>{sub}</div>}
  </div>
);

const PrivateCustomerDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ref: customerRef } = useParams();

  const { data: apiCustomer, loading, error, refetch } = usePrivateCustomerById(customerRef);
  const { execute: updateCustomer, loading: updateLoading, error: updateError, clearError: clearUpdateError } = useUpdatePrivateCustomer();

  const fromState = location.state?.customer ?? {};
  const customer = useMemo(() => {
    const mapped = mapApiToUi(apiCustomer, fromState);
    if (!mapped) return mockCustomer;
    return {
      ...mockCustomer,
      ...mapped,
      bookings: fromState.bookings ?? [],
      creditNotes: fromState.creditNotes ?? mockCustomer.creditNotes,
      notes: fromState.notes ?? mockCustomer.notes,
    };
  }, [apiCustomer, fromState.bookings, fromState.creditNotes, fromState.notes]);

  const c = customer;

  const [activeTab,     setActiveTab]     = useState(location.state?.defaultTab ?? "overview");
  const [noteText,      setNoteText]      = useState("");
  const [localNotes,    setLocalNotes]    = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    setLocalNotes(c.notes ?? []);
  }, [c.notes]);

  const handleEditOpen  = () => { clearUpdateError?.(); setEditModalOpen(true); };
  const handleEditClose = () => { clearUpdateError?.(); setEditModalOpen(false); };

  const handleEditSave = async (payload) => {
    if (!c.ref) return;
    try {
      await updateCustomer(c.ref, payload);
      setEditModalOpen(false);
      refetch();
    } catch (err) {
      console.error("Update customer failed:", err);
    }
  };

  const handleNewBooking = () => {
    navigate("/bookings/add", {
      state: {
        prefill: {
          customerType: "Private",
          customer: {
            id:            c.id,
            ref:           c.ref,
            name:          `${c.firstName} ${c.lastName}`,
            email:         c.email,
            phone:         c.phone,
            type:          "Private",
            country:       c.address?.country ?? c.nationality ?? "",
            totalBookings: c.bookings?.length ?? 0,
          },
        },
      },
    });
  };

  const initials = `${c.firstName[0]}${c.lastName[0]}`.toUpperCase();

  const handleBack = () => navigate("/people/customers/private");

  const TABS = [
    { key: "overview",   label: "Overview" },
    { key: "bookings",   label: "Bookings",   count: c.totalBookings ?? c.bookings?.length ?? 0 },
    { key: "financials", label: "Financials" },
    { key: "notes",      label: "Notes",      count: localNotes.length },
  ];

  if (customerRef && loading && !apiCustomer) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}>⏳</div>
          <p>Loading customer...</p>
        </div>
      </div>
    );
  }

  if (customerRef && error && !apiCustomer) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <p>{error}</p>
          <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
          <button className={styles.backBtn} onClick={handleBack}>← Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>

      <div className={styles.breadcrumb}>
        <button className={styles.backBtn} onClick={handleBack}>← Private Customers</button>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbCurrent}>{c.firstName} {c.lastName}</span>
      </div>

      <div className={styles.profileHeader}>
        <div className={styles.profileLeft}>
          <div className={styles.avatarLarge}>{initials}</div>
          <div className={styles.profileInfo}>
            <div className={styles.profileName}>{c.firstName} {c.lastName}</div>
            <div className={styles.profileRef}>{c.ref}</div>
            <div className={styles.profileMeta}>
              <span>📧 {c.email}</span>
              <span className={styles.metaDot}>·</span>
              <span>📞 {c.phone}</span>
              <span className={styles.metaDot}>·</span>
              <span>🌍 {c.nationality}</span>
              <span className={styles.metaDot}>·</span>
              <span>Since {c.createdAt}</span>
            </div>
          </div>
        </div>
        <div className={styles.profileActions}>
          <span className={`${styles.statusBadge} ${c.status === "Active" ? styles.statusActive : styles.statusInactive}`}>
            <span className={styles.statusDot} />
            {c.status}
          </span>
          <button className={styles.editBtn} onClick={handleEditOpen}>✏️ Edit</button>
          <button className={styles.addBookingBtn} onClick={handleNewBooking}>+ New Booking</button>
        </div>
      </div>

      <div className={styles.statsRow}>
        <StatCard label="Total Bookings" value={c.totalBookings ?? c.bookings?.length ?? 0} sub="All time" />
        <StatCard
          label="Total Spent"
          value={`€${c.financials.totalSpent.toLocaleString("en-EU", { minimumFractionDigits: 2 })}`}
          sub="All time"
        />
        <StatCard
          label="Open Balance"
          value={`€${c.financials.openBalance.toLocaleString("en-EU", { minimumFractionDigits: 2 })}`}
          sub="Outstanding"
          accent={c.financials.openBalance > 0}
        />
        <StatCard
          label="Credit Notes"
          value={`€${c.financials.creditNotes.toLocaleString("en-EU", { minimumFractionDigits: 2 })}`}
          sub="Available"
        />
      </div>

      <div className={styles.tabBar}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={`${styles.tabBadge} ${activeTab === tab.key ? styles.tabBadgeActive : ""}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className={styles.tabContent}>
          <div className={styles.twoCol}>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}>👤</span>
                <h3>Personal Information</h3>
                <button className={styles.cardEditBtn} onClick={handleEditOpen}>Edit</button>
              </div>
              <InfoRow label="First Name"    value={c.firstName} />
              <InfoRow label="Last Name"     value={c.lastName} />
              <InfoRow label="Date of Birth" value={c.dateOfBirth} />
              <InfoRow label="Gender"        value={c.gender} />
              <InfoRow label="Nationality"   value={c.nationality} />
              <InfoRow label="Language"      value={c.language} />
            </div>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}>📬</span>
                <h3>Contact Information</h3>
                <button className={styles.cardEditBtn} onClick={handleEditOpen}>Edit</button>
              </div>
              <InfoRow label="Email"     value={c.email} />
              <InfoRow label="Phone"     value={c.phone} />
              <InfoRow label="Street"    value={c.address.street} />
              <InfoRow label="House No." value={c.address.houseNumber} />
              <InfoRow label="Box No."   value={c.address.boxNumber} />
              <InfoRow label="City"      value={`${c.address.city}${c.address.zip ? `, ${c.address.zip}` : ""}`} />
              <InfoRow label="Country"   value={c.address.country} />
            </div>

          </div>

          <div className={styles.card} style={{ marginTop: 14 }}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>🗂️</span>
              <h3>Recent Bookings</h3>
              <button className={styles.cardLinkBtn} onClick={() => setActiveTab("bookings")}>
                View all →
              </button>
            </div>
            <table className={styles.miniTable}>
              <thead>
                <tr>
                  <th>Ref</th><th>Type</th><th>Service Dates</th>
                  <th>Price</th><th>Payment</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {c.bookings.slice(0, 3).map(b => {
                  const ts = TYPE_STYLES[b.type] || {};
                  return (
                    <tr key={b.ref}>
                      <td><span className={styles.miniRef}>{b.ref}</span></td>
                      <td>
                        <span className={styles.miniTypeBadge} style={{ background: ts.bg, color: ts.color }}>
                          {TYPE_ICON[b.type]} {b.type}
                        </span>
                      </td>
                      <td>
                        <span className={styles.miniDate}>{b.firstService}</span>
                        {b.lastService !== b.firstService && (
                          <span className={styles.miniDateSub}> → {b.lastService}</span>
                        )}
                      </td>
                      <td><strong>€{b.totalPrice.toLocaleString("en-EU", { minimumFractionDigits: 2 })}</strong></td>
                      <td><span className={`${styles.miniPayBadge} ${PAYMENT_BADGE[b.paymentStatus]}`}>{b.paymentStatus}</span></td>
                      <td><span className={`${styles.miniStatusBadge} ${STATUS_BADGE[b.bookingStatus]}`}>{b.bookingStatus}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "bookings" && (
        <div className={styles.tabContent}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>🗂️</span>
              <h3>All Bookings</h3>
              <span className={styles.cardCount}>{c.bookings.length} total</span>
            </div>
            <table className={styles.miniTable}>
              <thead>
                <tr>
                  <th>Ref</th><th>Type</th><th>First Service</th>
                  <th>Last Service</th><th>Price</th><th>Payment</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {c.bookings.map(b => {
                  const ts = TYPE_STYLES[b.type] || {};
                  return (
                    <tr key={b.ref}>
                      <td><span className={styles.miniRef}>{b.ref}</span></td>
                      <td>
                        <span className={styles.miniTypeBadge} style={{ background: ts.bg, color: ts.color }}>
                          {TYPE_ICON[b.type]} {b.type}
                        </span>
                      </td>
                      <td className={styles.miniDate}>{b.firstService}</td>
                      <td className={styles.miniDate}>{b.lastService}</td>
                      <td><strong>€{b.totalPrice.toLocaleString("en-EU", { minimumFractionDigits: 2 })}</strong></td>
                      <td><span className={`${styles.miniPayBadge} ${PAYMENT_BADGE[b.paymentStatus]}`}>{b.paymentStatus}</span></td>
                      <td><span className={`${styles.miniStatusBadge} ${STATUS_BADGE[b.bookingStatus]}`}>{b.bookingStatus}</span></td>
                      <td>
                        <button
                          className={styles.openBookingBtn}
                          onClick={() => navigate(`/bookings/${b.ref}`, { state: { booking: b, fromCustomer: true } })}
                        >
                          Open →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "financials" && (
        <div className={styles.tabContent}>
          <div className={styles.twoCol}>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}>💰</span>
                <h3>Financial Summary</h3>
              </div>
              <InfoRow label="Total Spent (all time)" value={`€${c.financials.totalSpent.toLocaleString("en-EU", { minimumFractionDigits: 2 })}`} />
              <InfoRow label="Open Balance"           value={`€${c.financials.openBalance.toLocaleString("en-EU", { minimumFractionDigits: 2 })}`} />
              <InfoRow label="Available Credit Notes" value={`€${c.financials.creditNotes.toLocaleString("en-EU", { minimumFractionDigits: 2 })}`} />
              <InfoRow label="Last Payment"           value={`€${c.financials.lastPaymentAmount.toLocaleString("en-EU", { minimumFractionDigits: 2 })} on ${c.financials.lastPayment}`} />
              {c.financials.openBalance > 0 && (
                <div className={styles.balanceWarning}>
                  ⚠️ This customer has an outstanding balance of{" "}
                  <strong>€{c.financials.openBalance.toLocaleString("en-EU", { minimumFractionDigits: 2 })}</strong>
                </div>
              )}
            </div>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.cardIcon}>🧾</span>
                <h3>Credit Notes</h3>
                <span className={styles.cardCount}>{c.creditNotes.length} note{c.creditNotes.length !== 1 ? "s" : ""}</span>
              </div>
              {c.creditNotes.length === 0 ? (
                <div className={styles.emptyMini}>No credit notes</div>
              ) : (
                c.creditNotes.map(cn => (
                  <div key={cn.ref} className={styles.creditNoteRow}>
                    <div className={styles.cnLeft}>
                      <div className={styles.cnRef}>{cn.ref}</div>
                      <div className={styles.cnReason}>{cn.reason}</div>
                      <div className={styles.cnDate}>{cn.date}</div>
                    </div>
                    <div className={styles.cnRight}>
                      <div className={styles.cnAmount}>€{cn.amount.toLocaleString("en-EU", { minimumFractionDigits: 2 })}</div>
                      <span className={styles.cnStatus}>{cn.status}</span>
                    </div>
                  </div>
                ))
              )}
            </div>

          </div>
        </div>
      )}

      {activeTab === "notes" && (
        <div className={styles.tabContent}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardIcon}>📝</span>
              <h3>Internal Notes</h3>
              <span className={styles.cardCount}>{localNotes.length} note{localNotes.length !== 1 ? "s" : ""}</span>
            </div>
            <div className={styles.addNoteBox}>
              <textarea
                className={styles.noteInput}
                placeholder="Add an internal note about this customer..."
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                rows={3}
              />
              <div className={styles.addNoteFooter}>
                <span className={styles.noteHint}>Visible only to staff</span>
                <button
                  className={styles.addNoteBtn}
                  disabled={!noteText.trim()}
                  onClick={() => {
                    setLocalNotes(prev => [
                      { id: Date.now(), author: "Admin", date: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }), text: noteText.trim() },
                      ...prev,
                    ]);
                    setNoteText("");
                  }}
                >
                  Add Note
                </button>
              </div>
            </div>
            <div className={styles.notesList}>
              {localNotes.map(n => (
                <div key={n.id} className={styles.noteItem}>
                  <div className={styles.noteHeader}>
                    <div className={styles.noteAuthorBadge}>{n.author[0]}</div>
                    <div>
                      <span className={styles.noteAuthor}>{n.author}</span>
                      <span className={styles.noteDate}>{n.date}</span>
                    </div>
                  </div>
                  <p className={styles.noteText}>{n.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <AddPrivateCustomerModal
        open={editModalOpen}
        onClose={handleEditClose}
        onSave={handleEditSave}
        customer={c}
        apiError={updateError}
      />

    </div>
  );
};

export default PrivateCustomerDetail;